import { Disclosure } from '@headlessui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import type { FormatJobType, JobCategory } from '@/api/cms/jobs/types';
import { useGetJobs, type GetJobsParams } from '@/api/cms/jobs/useGetJobs';
import type { DefaultCmsDataResponse, Location } from '@/api/cms/types';
import Accordion from '@/components/Accordion';
import Button from '@/components/Button';
import { MIN_PAGE_SIZE } from '@/lib/constants';
import useDebounce from '@/lib/useDebounce';
import useMediaQuery from '@/lib/useMediaQuery';
import EmptyIcon from '@/public/static/icons/recruitment/EmptyJobIcon.svg';
import CardJob from './CardJob';
import CardLoading from './CardLoading';
import JobPagination from './JobPagination';
import ListChipSelection from './ListChipSelection';

type Props = {
  jobTypes: DefaultCmsDataResponse<FormatJobType>[];
  locations: DefaultCmsDataResponse<Location>[];
  jobCategories: DefaultCmsDataResponse<JobCategory>[];
};

const schema = yup.object().shape({
  name: yup.string().optional(),
  department: yup.string().optional(),
  team: yup.string().optional(),
  // status: yup.array().optional(),
  level: yup.array().optional(),
  location: yup.array().optional(),
  product: yup.array().optional(),
  is_hot: yup.boolean().optional(),
  is_new: yup.boolean().optional(),
});
type FormJobSearchingBody = {
  name: string | undefined;
  department: string;
  team: string | undefined;
  location: string[] | string | undefined;
  product: string[];
  level: string[] | string | undefined;
  is_hot: boolean;
  is_new: boolean;
};
const NewJobPage = ({ jobTypes, locations, jobCategories }: Props) => {
  const router = useRouter();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { q, department, team, location, level, page, is_hot, is_new } =
    router.query;
  const [isHotJob, setIsHotJob] = useState<boolean>(
    is_hot === 'true' ? true : false
  );
  const [isNewJob, setIsNewJob] = useState<boolean>(
    is_new === 'true' ? true : false
  );

  const [pageJob, setPageJob] = useState(
    parseInt(page?.toString() ?? '1') ?? 1
  );
  const [dataForm, setDataForm] = useState<FormJobSearchingBody>({
    // page: pageJob,
    name: q?.toString() || '',
    department: '',
    team: '',
    // status: [],
    product: [],
    level: [],
    location: [],
    is_hot: false,
    is_new: false,
  });

  const [dataFormSubmit, setDataFormSubmit] = useState<FormJobSearchingBody>({
    // page: pageJob,
    name: '',
    department: '',
    team: '',
    // status: [],
    product: [],
    level: [],
    location: [],
    is_hot: false,
    is_new: false,
  });

  const debouncedValue = useDebounce<FormJobSearchingBody>(dataForm, 1500);

  const { register, handleSubmit, watch, setValue, setFocus } =
    useForm<FormJobSearchingBody>({
      resolver: yupResolver(schema),
      defaultValues: {
        name: debouncedValue.name
          ? debouncedValue.name
          : q?.toString()
          ? q.toString()
          : '',
        team: team?.toString() || '',
        location: location?.toString().split(',').map(String) || [],
        level: level?.toString().split(',').map(String) || [],
        is_hot: isHotJob,
        is_new: isNewJob,
      },
      mode: 'onChange',
    });
  const queryJobs: Partial<GetJobsParams> = {
    'pagination[page]': pageJob,
    'pagination[pageSize]': MIN_PAGE_SIZE,
    _q: debouncedValue.name
      ? debouncedValue.name
      : q?.toString()
      ? q.toString()
      : '',
    'filters[$and][0][is_new][$eq]': isNewJob
      ? isNewJob === true
        ? true
        : false
      : undefined,
    'filters[$and][0][is_hot][$eq]': isHotJob
      ? isHotJob === true
        ? true
        : false
      : undefined,
    'filters[job_types][id][$eq]': debouncedValue.department
      ? debouncedValue.department === 'all'
        ? undefined
        : debouncedValue.department
      : department
      ? department.toString() === 'all'
        ? undefined
        : department.toString()
      : undefined,
    'filters[job_categories][id][$in]':
      dataFormSubmit?.level && dataFormSubmit.level.length > 0
        ? dataFormSubmit?.level
        : watch('level')
        ? watch('level')
        : undefined,
    'filters[job_types][id][$in]':
      dataFormSubmit?.team && dataFormSubmit?.team.length > 0
        ? dataFormSubmit?.team !== '0'
          ? dataFormSubmit?.team
          : undefined
        : watch('team')
        ? watch('team') === 'all' || watch('team') === '0'
          ? undefined
          : watch('team')
        : undefined,
    'filters[locations][id][$in]':
      dataFormSubmit?.location && dataFormSubmit.location.length > 0
        ? dataFormSubmit?.location
        : watch('location')
        ? watch('location')
        : undefined,
  };

  const { data: jobsData, isFetching } = useGetJobs(queryJobs);

  const handleScrollToTop = () => {
    const viewAllSection = document.getElementById('job_banner');
    if (viewAllSection) {
      viewAllSection.scrollIntoView({
        behavior: 'smooth',
      });
    }
  };
  const onChangePagination = (pag: number) => {
    setPageJob(pag);
    handleScrollToTop();
  };
  const onSubmit: SubmitHandler<FormJobSearchingBody> = (data) => {
    setDataForm(data);
    setPageJob(1);
    setDataFormSubmit(data);
  };
  useEffect(() => {
    const levelValues = watch('level');
    const levelQueryString = Array.isArray(levelValues)
      ? levelValues.join(',')
      : levelValues;
    const locationValues = watch('location');
    const locationQueryString = Array.isArray(locationValues)
      ? locationValues.join(',')
      : locationValues;

    const newQuery = {
      pathname: '/job',
      query: {
        ...(debouncedValue.name !== '' || dataForm.name !== ''
          ? { q: debouncedValue.name || dataForm.name }
          : {}),
        ...(watch('team') !== '0' ? { team: watch('team') } : {}),
        location: locationQueryString
          ? [locationQueryString]
          : watch('location'),
        ...(isHotJob ? { is_hot: true } : {}),
        ...(isNewJob ? { is_new: true } : {}),
        level: levelQueryString ? [levelQueryString] : watch('level'),
        page: pageJob,
      },
    };

    if (JSON.stringify(newQuery) !== JSON.stringify(router.query)) {
      router.replace(newQuery, undefined, { shallow: true });
    }
  }, [
    debouncedValue.name,
    watch('department'),
    watch('team'),
    watch('location'),
    watch('level'),
    pageJob,
    isHotJob,
    isNewJob,
  ]);
  useEffect(() => {
    if (debouncedValue.name) {
      setFocus('name');
    }
  }, [debouncedValue.name, setFocus]);
  return (
    <div className="max-w-8xl z-40 mx-auto h-full w-full pb-[60px]">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div
          className={
            'shadow-shadowJobsite desktop:p-8 desktop:mt-[-52px] desktop:rounded-[8px] z-50  mx-auto mt-0 w-full max-w-4xl bg-white p-4'
          }
          id="newUiJob"
        >
          <div className=" desktop:flex-row flex w-full flex-col gap-4">
            <div className="desktop:basis-2/3 flex basis-1 flex-col gap-[8px]">
              <p className="text-[16px] font-bold">
                Tìm kiếm vị trí tuyển dụng
              </p>
              <input
                type="text"
                className=" text-body16 border-neutral-40 focus:ring-primary-40 form-input w-full rounded-[4px] border bg-white py-2 px-3 font-normal text-neutral-50 placeholder:text-base placeholder:font-normal placeholder:text-neutral-50 focus:border-white focus:ring-inset focus-visible:outline-none"
                placeholder="Nhập từ khoá hoặc tên vị trí ..."
                {...register('name')}
                onChange={(e) => {
                  setPageJob(1);
                  setDataForm((prevDataForm) => ({
                    ...prevDataForm,
                    name: e.target.value,
                  }));
                }}
              />
            </div>
            <div className=" desktop:basis-2/3 flex basis-1 flex-col gap-[8px]">
              <p className="text-[16px] font-bold">Chọn phòng ban</p>
              <select
                // {...register('department')}
                aria-label="Chon phong ban"
                defaultValue={watch('team')}
                className={
                  ' text-body16 border-neutral-40 focus:ring-primary-40 form-input w-full rounded-[4px] border bg-white py-2 px-3 font-normal text-neutral-50 placeholder:text-base placeholder:font-normal placeholder:text-neutral-50 focus:border-white focus:ring-inset focus-visible:outline-none'
                }
                onChange={(e) => {
                  setValue('team', e.target.value);
                  setPageJob(1);
                  setDataForm((prevDataForm) => ({
                    ...prevDataForm,
                    team: e.target.value,
                  }));
                }}
              >
                <option value={0}>Tất cả</option>
                {jobTypes.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.attributes.name}
                  </option>
                ))}
              </select>
            </div>
            {isMobile && (
              <Accordion
                expandContent="Tìm kiếm nâng cao"
                collapseContent="Thu gọn"
              >
                <div className="divide-y divide-[#E6E9EC] rounded-2xl border-[1px] border-solid border-[#E6E9EC] bg-white ">
                  <div className="p-4">
                    <Disclosure defaultOpen={true}>
                      {({ open }) => (
                        <>
                          <Disclosure.Button className="flex w-full justify-between rounded-lg bg-purple-100 text-left text-[16px] font-bold">
                            <span>Phòng ban</span>
                            {open ? (
                              <div
                                className="h-5 w-5 shrink-0 rounded-full border bg-center bg-no-repeat"
                                style={{
                                  backgroundImage:
                                    'url(/static/icons/MinusCircle.svg)',
                                }}
                              ></div>
                            ) : (
                              <div
                                className="h-5 w-5 shrink-0 rounded-full border bg-center bg-no-repeat"
                                style={{
                                  backgroundImage:
                                    'url(/static/icons/PlusCircle.svg)',
                                }}
                              ></div>
                            )}
                          </Disclosure.Button>
                          <Disclosure.Panel className="flex flex-col gap-[16px] pt-8 pb-2 text-[16px]">
                            <div key={'all'} className="flex gap-2">
                              <input
                                type="radio"
                                value={0}
                                defaultChecked={watch('team') ? false : true}
                                id={'all' + 'team'}
                                {...register('team', {
                                  onChange: () => {
                                    setPageJob(1);
                                    handleScrollToTop();
                                  },
                                })}
                              />
                              <label htmlFor={'all' + 'team'}>Tất cả</label>
                            </div>
                            {jobTypes.map((jobType) => {
                              return (
                                <div key={jobType.id} className="flex gap-2">
                                  <input
                                    type="radio"
                                    value={jobType.id}
                                    id={jobType.id + 'team'}
                                    {...register('team', {
                                      onChange: () => {
                                        setPageJob(1);
                                        handleScrollToTop();
                                      },
                                    })}
                                  />
                                  <label htmlFor={jobType.id + 'team'}>
                                    {jobType.attributes.name}
                                  </label>
                                </div>
                              );
                            })}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  </div>
                  <div className="p-4">
                    <Disclosure as="div" defaultOpen={true}>
                      {({ open }) => (
                        <>
                          <Disclosure.Button className="flex w-full justify-between rounded-lg bg-purple-100 text-left text-[16px] font-bold">
                            <span>Trạng thái</span>
                            {open ? (
                              <div
                                className="h-5 w-5 shrink-0 rounded-full border bg-center bg-no-repeat"
                                style={{
                                  backgroundImage:
                                    'url(/static/icons/MinusCircle.svg)',
                                }}
                              ></div>
                            ) : (
                              <div
                                className="h-5 w-5 shrink-0 rounded-full border bg-center bg-no-repeat"
                                style={{
                                  backgroundImage:
                                    'url(/static/icons/PlusCircle.svg)',
                                }}
                              ></div>
                            )}
                          </Disclosure.Button>
                          <Disclosure.Panel className="flex flex-col gap-[16px] pt-8 pb-2 text-[16px]">
                            <div className="flex gap-2">
                              <input
                                id={'is_hot'}
                                type="checkbox"
                                value={'is_hot'}
                                {...register('is_hot', {
                                  onChange: () => {
                                    setIsHotJob(!isHotJob);
                                    setPageJob(1);
                                  },
                                })}
                                checked={isHotJob ?? is_hot}
                              />
                              <label htmlFor={'is_hot'}>Hot Job</label>
                            </div>
                            <div className="flex gap-2">
                              <input
                                id={'is_new'}
                                type="checkbox"
                                value={'is_new'}
                                {...register('is_new', {
                                  onChange: () => {
                                    setIsNewJob(!isNewJob);
                                    setPageJob(1);
                                  },
                                })}
                                checked={isNewJob ?? is_new} // Reflect the state in the checkbox
                              />
                              <label htmlFor={'is_new'}>New Job</label>
                            </div>
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  </div>
                  <div className="p-4">
                    <Disclosure defaultOpen={true}>
                      {({ open }) => (
                        <>
                          <Disclosure.Button className="flex w-full justify-between rounded-lg bg-purple-100 text-left text-[16px] font-bold">
                            <span>Địa điểm</span>
                            {open ? (
                              <div
                                className="h-5 w-5 shrink-0 rounded-full border bg-center bg-no-repeat"
                                style={{
                                  backgroundImage:
                                    'url(/static/icons/MinusCircle.svg)',
                                }}
                              ></div>
                            ) : (
                              <div
                                className="h-5 w-5 shrink-0 rounded-full border bg-center bg-no-repeat"
                                style={{
                                  backgroundImage:
                                    'url(/static/icons/PlusCircle.svg)',
                                }}
                              ></div>
                            )}
                          </Disclosure.Button>
                          <Disclosure.Panel className="flex flex-col gap-[16px] pt-8 pb-2 text-[16px]">
                            {locations.map((location) => {
                              return (
                                <div key={location.id} className="flex gap-2">
                                  <input
                                    type="checkbox"
                                    value={location.id}
                                    id={location.id + 'location'}
                                    {...register('location', {
                                      onChange: () => {
                                        setPageJob(1);
                                      },
                                    })}
                                  />
                                  <label htmlFor={location.id + 'location'}>
                                    {location.attributes.name}
                                  </label>
                                </div>
                              );
                            })}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  </div>
                  <div className="p-4">
                    <Disclosure defaultOpen={true}>
                      {({ open }) => (
                        <>
                          <Disclosure.Button className="flex w-full justify-between rounded-lg bg-purple-100 text-left text-[16px] font-bold">
                            <span>Cấp độ</span>
                            {open ? (
                              <div
                                className="h-5 w-5 shrink-0 rounded-full border bg-center bg-no-repeat"
                                style={{
                                  backgroundImage:
                                    'url(/static/icons/MinusCircle.svg)',
                                }}
                              ></div>
                            ) : (
                              <div
                                className="h-5 w-5 shrink-0 rounded-full border bg-center bg-no-repeat"
                                style={{
                                  backgroundImage:
                                    'url(/static/icons/PlusCircle.svg)',
                                }}
                              ></div>
                            )}
                          </Disclosure.Button>
                          <Disclosure.Panel className="flex flex-col gap-[16px] pt-8 pb-2 text-[16px]">
                            {jobCategories.map((cate) => {
                              return (
                                <div key={cate.id} className="flex gap-2">
                                  <input
                                    type="checkbox"
                                    value={cate.id}
                                    id={cate.id + 'level'}
                                    {...register('level', {
                                      onChange: () => {
                                        setPageJob(1);
                                      },
                                    })}
                                  />
                                  <label htmlFor={cate.id + 'level'}>
                                    {cate.attributes.name}
                                  </label>
                                </div>
                              );
                            })}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  </div>
                </div>
              </Accordion>
            )}
            <Button
              type="submit"
              className=" desktop:h-[42px] desktop:self-end desktop:mt-0"
            >
              Tìm kiếm
            </Button>
          </div>
        </div>
        <div className="desktop:px-8 desktop:gap-1 desktop:pb-[48px] max-w-8xl flex flex-col gap-[24px] px-4 pt-[60px] pb-[24px] ">
          <p className="desktop:text-[42px] desktop:text-left text-left text-[32px] font-bold">
            Ahamovers - Những chiến binh không ngừng chuyển động!
          </p>
          <p className="desktop:text-[18px] desktop:text-left text-left text-[14px] font-normal">
            Hãy chuyển động cùng Ahamove để chinh phục những đỉnh cao mới!
          </p>
          {isMobile && (
            <ListChipSelection
              jobCategories={jobCategories}
              jobTypes={jobTypes}
              locations={locations}
            />
          )}
        </div>
        <div className="desktop:flex-row max-w-8xl desktop:px-8 mx-auto  flex w-full flex-col items-start justify-between gap-8"></div>
        <div className="desktop:flex-row max-w-8xl desktop:px-8 mx-auto  flex w-full flex-col items-start justify-between gap-8">
          {!isMobile && (
            <div className="desktop:max-w-[287px] flex flex-col gap-3">
              <ListChipSelection
                jobCategories={jobCategories}
                jobTypes={jobTypes}
                locations={locations}
              />
              <div className="desktop:max-w-[287px]  desktop:rounded-[8px] w-full border-[1px] border-solid border-[#E6E9EC]">
                <div className=" desktop:w-[285px] w-full divide-y divide-[#E6E9EC] rounded-2xl bg-white ">
                  <div className="p-4">
                    <Disclosure defaultOpen={true}>
                      {({ open }) => (
                        <>
                          <Disclosure.Button className="flex w-full justify-between rounded-lg bg-purple-100 text-left text-[16px] font-bold">
                            <span>Phòng ban</span>
                            {open ? (
                              <div
                                className="h-5 w-5 shrink-0 rounded-full border bg-center bg-no-repeat"
                                style={{
                                  backgroundImage:
                                    'url(/static/icons/MinusCircle.svg)',
                                }}
                              ></div>
                            ) : (
                              <div
                                className="h-5 w-5 shrink-0 rounded-full border bg-center bg-no-repeat"
                                style={{
                                  backgroundImage:
                                    'url(/static/icons/PlusCircle.svg)',
                                }}
                              ></div>
                            )}
                          </Disclosure.Button>
                          <Disclosure.Panel className="flex flex-col gap-[16px] pt-8 pb-2 text-[16px]">
                            <div key={'all'} className="flex gap-2">
                              <input
                                type="radio"
                                value={0}
                                defaultChecked={watch('team') ? false : true}
                                id={'all' + 'team'}
                                {...register('team', {
                                  onChange: () => {
                                    setPageJob(1);
                                    handleScrollToTop();
                                  },
                                })}
                              />
                              <label htmlFor={'all' + 'team'}>Tất cả</label>
                            </div>
                            {jobTypes.map((jobType) => {
                              return (
                                <div key={jobType.id} className="flex gap-2">
                                  <input
                                    type="radio"
                                    value={jobType.id}
                                    id={jobType.id + 'team'}
                                    {...register('team', {
                                      onChange: () => {
                                        setPageJob(1);
                                        handleScrollToTop();
                                      },
                                    })}
                                  />
                                  <label htmlFor={jobType.id + 'team'}>
                                    {jobType.attributes.name}
                                  </label>
                                </div>
                              );
                            })}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  </div>
                  <div className="p-4">
                    <Disclosure as="div" className="mt-2" defaultOpen={true}>
                      {({ open }) => (
                        <>
                          <Disclosure.Button className="flex w-full justify-between rounded-lg bg-purple-100 text-left text-[16px] font-bold">
                            <span>Trạng thái</span>
                            {open ? (
                              <div
                                className="h-5 w-5 shrink-0 rounded-full border bg-center bg-no-repeat"
                                style={{
                                  backgroundImage:
                                    'url(/static/icons/MinusCircle.svg)',
                                }}
                              ></div>
                            ) : (
                              <div
                                className="h-5 w-5 shrink-0 rounded-full border bg-center bg-no-repeat"
                                style={{
                                  backgroundImage:
                                    'url(/static/icons/PlusCircle.svg)',
                                }}
                              ></div>
                            )}
                          </Disclosure.Button>
                          <Disclosure.Panel className="flex flex-col gap-[16px] pt-8 pb-2 text-[16px]">
                            <div className="flex gap-2">
                              <input
                                id={'is_hot'}
                                type="checkbox"
                                value={'is_hot'}
                                onChange={() => {
                                  handleScrollToTop();
                                  setIsHotJob(!isHotJob);
                                  setPageJob(1);
                                }}
                                checked={isHotJob ?? is_hot}
                              />
                              <label htmlFor={'is_hot'}>Hot Job</label>
                            </div>
                            <div className="flex gap-2">
                              <input
                                id={'is_new'}
                                type="checkbox"
                                value={'is_new'}
                                onChange={() => {
                                  handleScrollToTop();
                                  setIsNewJob(!isNewJob);
                                  setPageJob(1);
                                }}
                                checked={isNewJob ?? is_new}
                              />
                              <label htmlFor={'is_new'}>New Job</label>
                            </div>
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  </div>
                  <div className="p-4">
                    <Disclosure defaultOpen={true}>
                      {({ open }) => (
                        <>
                          <Disclosure.Button className="flex w-full justify-between rounded-lg bg-purple-100 text-left text-[16px] font-bold">
                            <span>Địa điểm</span>
                            {open ? (
                              <div
                                className="h-5 w-5 shrink-0 rounded-full border bg-center bg-no-repeat"
                                style={{
                                  backgroundImage:
                                    'url(/static/icons/MinusCircle.svg)',
                                }}
                              ></div>
                            ) : (
                              <div
                                className="h-5 w-5 shrink-0 rounded-full border bg-center bg-no-repeat"
                                style={{
                                  backgroundImage:
                                    'url(/static/icons/PlusCircle.svg)',
                                }}
                              ></div>
                            )}
                          </Disclosure.Button>
                          <Disclosure.Panel className="flex flex-col gap-[16px] pt-8 pb-2 text-[16px]">
                            {locations.map((location) => {
                              return (
                                <div key={location.id} className="flex gap-2">
                                  <input
                                    // {...register('location')}
                                    type="checkbox"
                                    value={location.id}
                                    id={location.id + 'location'}
                                    {...register('location', {
                                      onChange: () => {
                                        setPageJob(1);
                                        handleScrollToTop();
                                      },
                                    })}
                                  />
                                  <label htmlFor={location.id + 'location'}>
                                    {location.attributes.name}
                                  </label>
                                </div>
                              );
                            })}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  </div>
                  <div className="p-4">
                    <Disclosure defaultOpen={true}>
                      {({ open }) => (
                        <>
                          <Disclosure.Button className="flex w-full justify-between rounded-lg bg-purple-100 text-left text-[16px] font-bold">
                            <span>Cấp độ </span>
                            {open ? (
                              <div
                                className="h-5 w-5 shrink-0 rounded-full border bg-center bg-no-repeat"
                                style={{
                                  backgroundImage:
                                    'url(/static/icons/MinusCircle.svg)',
                                }}
                              ></div>
                            ) : (
                              <div
                                className="h-5 w-5 shrink-0 rounded-full border bg-center bg-no-repeat"
                                style={{
                                  backgroundImage:
                                    'url(/static/icons/PlusCircle.svg)',
                                }}
                              ></div>
                            )}
                          </Disclosure.Button>
                          <Disclosure.Panel className="flex flex-col gap-[16px] pt-8 pb-2 text-[16px]">
                            {jobCategories.map((cate) => {
                              return (
                                <div key={cate.id} className="flex gap-2">
                                  <input
                                    // {...register('level')}
                                    type="checkbox"
                                    value={cate.id}
                                    id={cate.id + 'level'}
                                    {...register('level', {
                                      onChange: () => {
                                        setPageJob(1);
                                        handleScrollToTop();
                                      },
                                    })}
                                  />
                                  <label htmlFor={cate.id + 'level'}>
                                    {cate.attributes.name}
                                  </label>
                                </div>
                              );
                            })}
                          </Disclosure.Panel>
                        </>
                      )}
                    </Disclosure>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="desktop:px-0 flex w-full flex-col gap-[28px] px-4">
            {jobsData?.meta?.pagination.total &&
            jobsData?.meta.pagination.total > 0 ? (
              <p className="text-left text-[18px] font-bold">
                Vị trí tuyển dụng ({jobsData?.meta.pagination.total})
              </p>
            ) : isFetching ? (
              <p className="text-left text-[18px] font-bold">
                Vị trí tuyển dụng
              </p>
            ) : (
              <></>
            )}
            <div className="flex w-full flex-col gap-[24px]">
              {isFetching ? (
                Array.from({ length: 4 }).map((_, index) => (
                  <CardLoading type="vertical" key={index} />
                ))
              ) : (
                <>
                  {jobsData?.data && jobsData.data.length > 0 ? (
                    jobsData?.data.map((job) => {
                      return (
                        <CardJob
                          searchContext={dataForm.name}
                          key={job.id}
                          type="vertical"
                          jobItem={job}
                        />
                      );
                    })
                  ) : (
                    <div className="flex h-[376px] flex-col items-center justify-center gap-4 align-middle">
                      <EmptyIcon />
                      <p className="text-[14px] text-[#4D5358]">
                        Không tìm thấy job phù hợp với yêu cầu
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
            {jobsData && jobsData.data && jobsData?.data.length > 0 ? (
              <JobPagination
                key={jobsData.meta.pagination.page}
                total={jobsData?.meta.pagination.total}
                page={pageJob}
                pageCount={jobsData?.meta.pagination.pageCount}
                pageSize={jobsData?.meta.pagination.pageSize}
                onChangePagination={onChangePagination}
              />
            ) : (
              <></>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};
export default NewJobPage;
